import { Pagination } from "@eworkit/iris";
import React, { FC, memo, RefObject, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { JobRequestListDto, JobRequestListDtoPageable } from "@/data/job-requests/types/JobRequest";
import { Flex } from "@/eworkitComponents";
import { IPageableFetcherRenderProps } from "@/hooks/http";
import { InfoboxSection } from "@/views/components/Page";
import { withHttpError } from "@/views/Errors";

import { JobRequestRecord } from "./JobRequestRecord";

export type TJobRequestRecordVariant = "default" | "short";

export interface IJobRequestListRecordProps {
  record: JobRequestListDto;
  variant?: TJobRequestRecordVariant;
  onUpdate?: (record: JobRequestListDto) => void;
  trackingSource: string;
}

interface IJobRequestListRendererComponentProps
  extends IPageableFetcherRenderProps<JobRequestListDto, JobRequestListDtoPageable> {
  recordVariant?: TJobRequestRecordVariant;
  hidePagination?: boolean;
  onRecordUpdate?: (record: JobRequestListDto) => void;
  renderEmpty?: () => React.ReactElement;
  trackingSource: string;
  lastJobOfferRef?: RefObject<HTMLDivElement>;
}

const JobRequestsListRendererComponent: FC<IJobRequestListRendererComponentProps> = memo(
  ({
    payload,
    updateRecord,
    changePage,
    changePageSize,
    recordVariant,
    hidePagination,
    onRecordUpdate,
    renderEmpty,
    trackingSource,
    lastJobOfferRef,
  }) => {
    const { t } = useTranslation();

    const handleRecordUpdate = useCallback(
      (record: JobRequestListDto) => {
        const item = payload?.content.find(cp => cp.data.id === record.id);
        if (item) {
          updateRecord({ ...item, data: record });
          onRecordUpdate?.(record);
        }
      },
      [payload, updateRecord],
    );

    const renderRecord = useCallback(
      (record: JobRequestListDto, index: number) => {
        const isLastElement = index + 1 === payload?.content.length;

        return (
          <JobRequestRecord
            lastJobOfferRef={isLastElement ? lastJobOfferRef : undefined}
            key={`job-request-${record.id}`}
            variant={recordVariant}
            record={record}
            onUpdate={handleRecordUpdate}
            trackingSource={trackingSource}
          />
        );
      },
      [recordVariant, handleRecordUpdate, trackingSource],
    );

    if (!payload) {
      return null;
    }

    if (payload.content && !payload.content.length) {
      return renderEmpty?.() || <InfoboxSection />;
    }

    return (
      <Flex innerMargin="md" direction="column">
        {payload.content.map((record, index) => renderRecord(record.data, index))}
        {!hidePagination && payload.totalPages > 1 && (
          <Pagination
            pagesCount={payload?.totalPages ?? 0}
            activeIndex={payload?.number ?? 0}
            onChangeIndex={changePage}
            pageSize={payload?.size ?? 0}
            showPageSizeSelect={false}
            onChangePageSize={changePageSize}
            resultsLabel={t("public:COMPONENTS.PAGINATION.RESULTS")}
          />
        )}
        {/* TODO error case */}
      </Flex>
    );
  },
);

export const JobRequestsListRenderer = withHttpError(JobRequestsListRendererComponent);
